import React, { useEffect } from 'react';
import { KEYS, getIsPrintableCharacter } from '../../common/keyCodes';
import debounce from 'lodash/debounce';
import { DropdownOptionProps } from '../Dropdown';

const DELAY_TOLERANCE = 500;

const filterOptions = (options: any[], input: string) =>
  options.filter((option) =>
    option.value?.toLocaleLowerCase().startsWith(input.toLocaleLowerCase()),
  );

export const useMenuPatternKeys = ({
  options,
  onSelect,
  isOpen,
  setIsOpen,
  focusedOptionId,
  setFocusedOptionId,
}: {
  options: DropdownOptionProps[];
  onSelect: (id: string) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  focusedOptionId: string | null;
  setFocusedOptionId: (id: string) => void;
}) => {
  const [input, setInput] = React.useState('');

  const clearInputDebounced = React.useCallback(
    debounce(() => setInput(''), DELAY_TOLERANCE),
    [setInput],
  );

  useEffect(
    () => () => {
      clearInputDebounced.cancel();
    },
    [],
  );

  const matchedOptions = React.useMemo(
    () => filterOptions(options, input),
    [options, input],
  );

  const isValidSelection = (id: string) => {
    const option = options.find((opt) => opt.id === id);
    return option?.isSelectable && !option.isDisabled;
  };

  const safeSetFocusedOptionId = (id: string | undefined) => {
    if (id !== undefined) {
      setFocusedOptionId(id);
    }
  };
  const open = (e: React.KeyboardEvent) => {
    setIsOpen(true);
    e.preventDefault();
  };

  const handleLetters = (key: string) => {
    if (key === input) {
      const currentMatchIndex = matchedOptions.findIndex(
        (option) => option.id === focusedOptionId,
      );
      const nextMatchIndex = (currentMatchIndex + 1) % matchedOptions.length;
      const nextMatch = matchedOptions[nextMatchIndex];
      safeSetFocusedOptionId(nextMatch.id);
      clearInputDebounced.cancel();
    } else {
      const newInput = (input + key).toLocaleLowerCase();
      const newMatchedOptions = filterOptions(options, newInput);
      if (newMatchedOptions.length > 0) {
        safeSetFocusedOptionId(newMatchedOptions[0].id);
      }
      setInput(newInput);
      clearInputDebounced();
    }
  };

  const focusFirstOption = () => {
    safeSetFocusedOptionId(options[0].id);
  };

  const focusLastOption = () => {
    safeSetFocusedOptionId(options[options.length - 1].id);
  };

  const getFocusedOptionIndex = () =>
    focusedOptionId
      ? options.findIndex((option) => option.id === focusedOptionId)
      : -1;

  const focusNextOption = () => {
    const currentIndex = getFocusedOptionIndex();

    for (let index = currentIndex + 1; index < options.length; index++) {
      const nextOption = options[index];
      if (nextOption.divider) {
        continue;
      }

      safeSetFocusedOptionId(nextOption.id);
      break;
    }
    if (currentIndex + 1 === options.length) {
      focusFirstOption();
    }
  };

  const focusPreviousOption = () => {
    const focusedOptionIndex = getFocusedOptionIndex();
    const currentIndex =
      focusedOptionIndex === -1 ? options.length : focusedOptionIndex;

    for (let index = currentIndex - 1; index >= 0; index--) {
      const previousOption = options[index];
      if (previousOption.divider) {
        continue;
      }
      safeSetFocusedOptionId(previousOption.id);
      break;
    }
    if (currentIndex === 0) {
      focusLastOption();
    }
  };

  const selectFocused = () => {
    if (focusedOptionId && isValidSelection(focusedOptionId)) {
      onSelect(focusedOptionId);
    }
  };

  const handleMenuKeydown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case KEYS.Spacebar:
      case KEYS.Enter:
        selectFocused();
        event.preventDefault();
        break;
      case KEYS.Escape:
      case KEYS.Tab:
        setIsOpen(false);
        break;
      case KEYS.ArrowDown:
        event.preventDefault();
        focusNextOption();
        break;
      case KEYS.ArrowUp:
        event.preventDefault();
        if (event.altKey) {
          selectFocused();
          setIsOpen(false);
        } else {
          focusPreviousOption();
        }
        break;
      case KEYS.Home:
        event.preventDefault();
        focusFirstOption();
        break;
      case KEYS.End:
        event.preventDefault();
        focusLastOption();
        break;
    }
  };

  const handleMenuTriggerKeydown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case KEYS.Spacebar:
      case KEYS.Enter:
      case KEYS.ArrowDown:
        focusFirstOption();
        open(event);
        break;
      case KEYS.ArrowUp:
        focusLastOption();
        open(event);
        break;
      default:
        return;
    }
  };

  const handleKeydown = (event: React.KeyboardEvent) => {
    const { key } = event;
    if (getIsPrintableCharacter(key) && key !== KEYS.Spacebar) {
      if (!isOpen) {
        open(event);
      }
      handleLetters(key);
      return;
    }
    if (isOpen) {
      handleMenuKeydown(event);
    } else {
      handleMenuTriggerKeydown(event);
    }
  };

  return {
    handleKeydown,
  };
};
